import { memo, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, Star, Tag, Bell, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchFeaturedCourses } from "@/services/courseService";
import { fetchActivePromotions, getPromotionForCourse } from "@/services/promotionService";
import { SkeletonCourseGrid } from "@/components/ui/skeleton-course-card";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  is_coming_soon?: boolean;
  estimated_launch_date?: string;
  early_bird_discount?: number;
  activePromotion?: {
    name: string;
    discount_percentage: number;
    end_date: string;
  };
  discounted_price?: number;
}

const FeaturedCourses = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [interestedCourses, setInterestedCourses] = useState<Set<string>>(new Set());
  const { measureAsync } = usePerformanceMonitor("FeaturedCourses");
  const { session } = useAuth();

  useEffect(() => {
    loadFeaturedCourses();
  }, [session]);

  const loadFeaturedCourses = async () => {
    try {
      const userData = await supabase.auth.getUser();
      
      // Fetch ALL data in parallel including course_interest
      const [publishedCourses, comingSoonResult, allPromotions, interestData] = await Promise.all([
        measureAsync("fetchFeaturedCourses", fetchFeaturedCourses),
        supabase.from("courses").select("*").eq("is_coming_soon", true).limit(2),
        fetchActivePromotions(),
        userData.data.user
          ? supabase
              .from("course_interest")
              .select("course_id")
              .eq("user_id", userData.data.user.id)
          : Promise.resolve({ data: [] })
      ]);

      const allCourses = [...publishedCourses, ...(comingSoonResult.data || [])];
      
      // Map promotions to courses (in-memory)
      const coursesWithPromotions = allCourses.map((course) => {
        // Skip promotion for coming soon courses
        if (course.is_coming_soon) {
          return course;
        }

        const activePromo = getPromotionForCourse(course.id, allPromotions);

        if (activePromo) {
          const discountedPrice = course.price * (1 - activePromo.discount_percentage / 100);
          return {
            ...course,
            activePromotion: {
              name: activePromo.name,
              discount_percentage: activePromo.discount_percentage,
              end_date: activePromo.end_date,
            },
            discounted_price: discountedPrice,
          };
        }
        
        return course;
      });

      setCourses(coursesWithPromotions);
      
      // Set user interests (already fetched in parallel)
      if (interestData.data) {
        setInterestedCourses(new Set(interestData.data.map(i => i.course_id)));
      }
    } catch (error) {
      console.error("Error loading courses:", error);
    }
    setIsLoading(false);
  };

  const handleNotifyMe = async (courseId: string) => {
    if (!session?.user) {
      toast.error("Please sign in to get notified");
      return;
    }

    try {
      if (interestedCourses.has(courseId)) {
        // Remove interest
        await supabase
          .from("course_interest")
          .delete()
          .eq("user_id", session.user.id)
          .eq("course_id", courseId);
        
        setInterestedCourses(prev => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
        toast.success("Notification removed");
      } else {
        // Add interest
        await supabase
          .from("course_interest")
          .insert({
            user_id: session.user.id,
            course_id: courseId
          });
        
        setInterestedCourses(prev => new Set([...prev, courseId]));
        toast.success("You'll be notified when this course launches!");
      }
    } catch (error) {
      console.error("Error updating course interest:", error);
      toast.error("Failed to update notification preference");
    }
  };

  if (isLoading) {
    return (
      <section className="py-20 lg:py-32 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px] -z-10" />
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-6 max-w-3xl mx-auto">
            <Badge variant="secondary" className="inline-flex items-center gap-2 px-4 py-2 text-sm backdrop-blur-md">
              <Star className="w-4 h-4 fill-primary text-primary" />
              {t('featuredCourses.badge')}
            </Badge>
          </div>
          <SkeletonCourseGrid count={3} />
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px] -z-10" />
      
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header with proper spacing */}
        <div className="text-center mb-16 space-y-6 max-w-3xl mx-auto">
          <Badge variant="secondary" className="inline-flex items-center gap-2 px-4 py-2 text-sm backdrop-blur-md">
            <Star className="w-4 h-4 fill-primary text-primary" />
            {t('featuredCourses.badge')}
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              {t('featuredCourses.title')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('featuredCourses.subtitle')}
          </p>
        </div>

        {/* Courses Grid with proper spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {courses.map((course, index) => (
            <Card 
              key={course.id} 
              className={`group animate-fade-in overflow-hidden ${
                course.is_coming_soon ? 'opacity-90 border-accent/50' : ''
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Course Thumbnail */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={course.thumbnail_url} 
                  alt={course.title}
                  loading="lazy"
                  width="400"
                  height="192"
                  className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 ${
                    course.is_coming_soon ? 'grayscale-[40%]' : ''
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between flex-wrap gap-2">
                  {course.is_coming_soon ? (
                    <>
                      <Badge className="bg-accent text-accent-foreground backdrop-blur-md gap-1">
                        <Calendar className="w-3 h-3" />
                        {t('featuredCourses.comingSoon')}
                      </Badge>
                      {course.early_bird_discount && course.early_bird_discount > 0 && (
                        <Badge className="bg-primary text-primary-foreground backdrop-blur-md">
                          {course.early_bird_discount}% {t('featuredCourses.offAtLaunch')}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <>
                      <Badge variant="secondary" className="backdrop-blur-md">
                        {course.category}
                      </Badge>
                      <Badge variant="outline" className="backdrop-blur-md">
                        {course.level}
                      </Badge>
                      {course.activePromotion && (
                        <Badge variant="default" className="backdrop-blur-md gap-1">
                          <Tag className="w-3 h-3" />
                          {course.activePromotion.discount_percentage}% {t('popularCourses.off')}
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>

              <CardHeader className="space-y-3">
                <CardTitle className="group-hover:text-primary transition-colors line-clamp-2">
                  {course.title}
                </CardTitle>
                <CardDescription className="line-clamp-3 text-sm leading-relaxed">
                  {course.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {!course.is_coming_soon && (
                  <>
                    {/* Course Stats with proper spacing */}
                    <div className="grid grid-cols-3 gap-4 py-4 border-y border-white/10">
                      <div className="flex items-center gap-2 justify-center">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{course.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center border-x border-white/10">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{course.students_count}</span>
                      </div>
                      <div className="flex items-center gap-2 justify-center">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm text-muted-foreground">{course.rating}</span>
                      </div>
                    </div>

                    {/* Price with proper spacing */}
                    <div>
                      {course.discounted_price ? (
                        <>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                              ${course.discounted_price.toFixed(2)}
                            </span>
                            <span className="text-lg text-muted-foreground line-through">
                              ${course.price}
                            </span>
                          </div>
                          <p className="text-sm text-green-600 font-medium">
                            {t('featuredCourses.save')} ${(course.price - course.discounted_price).toFixed(2)}!
                          </p>
                        </>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                            ${course.price}
                          </span>
                          <span className="text-sm text-muted-foreground">{t('featuredCourses.perCourse')}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {course.is_coming_soon && course.estimated_launch_date && (
                <div className="py-4 border-y border-white/10">
                    <p className="text-sm text-muted-foreground text-center">
                      {t('featuredCourses.expectedLaunch')} {new Date(course.estimated_launch_date).toLocaleDateString('en-US', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                )}
              </CardContent>

              <CardFooter className="pt-0">
                {course.is_coming_soon ? (
                  <Button 
                    variant={interestedCourses.has(course.id) ? "outline" : "default"}
                    className="w-full"
                    onClick={() => handleNotifyMe(course.id)}
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    {interestedCourses.has(course.id) ? t('featuredCourses.notificationActive') : t('featuredCourses.notifyMe')}
                  </Button>
                ) : (
                  <Link to={`/courses/${course.slug || course.id}`} className="w-full">
                    <Button 
                      variant="default" 
                      className="w-full group-hover:shadow-glass-hover"
                    >
                      {t('featuredCourses.viewCourse')}
                    </Button>
                  </Link>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* View All Button with proper spacing */}
        <div className="text-center pt-8">
          <Link to="/courses">
            <Button variant="outline" size="lg" className="min-w-[200px]">
              {t('featuredCourses.viewAllCourses')}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default memo(FeaturedCourses);
